<!-- The whole card picks the design: a button stretched under the content.
     Content ignores pointer events so clicks reach it; only the real controls
     (issues hint, Bearbeiten, menu) sit above it and take their own clicks. -->
<button
  type="button"
  class="focus-visible:ring-ring/50 absolute inset-0 rounded-lg outline-none focus-visible:ring-[3px]"
  [class.cursor-pointer]="selectable"
  [class.cursor-default]="!selectable"
  [attr.aria-pressed]="isActive()"
  [attr.aria-disabled]="!isActive() && !selectable ? 'true' : null"
  [attr.aria-label]="selectLabel"
  (click)="onActivate()"></button>

<!-- Phones: a row with a small thumbnail; from sm up: a card with the thumbnail on top. -->
<div class="pointer-events-none relative w-20 shrink-0 sm:w-full">
  <app-design-thumbnail [design]="design()" />

  @if (working()) {
    <span
      class="bg-background absolute end-1.5 top-1.5 flex size-6 items-center justify-center rounded-full shadow-sm"
      aria-hidden="true">
      <hlm-spinner class="size-3.5" />
    </span>
  } @else if (isActive()) {
    <!-- The design in use: a white check on the primary colour -->
    <span
      class="bg-primary text-primary-foreground absolute end-1.5 top-1.5 flex h-6 items-center gap-1 rounded-full ps-1.5 pe-2.5 text-xs font-medium shadow-sm"
      aria-hidden="true">
      <ng-icon name="lucideCheck" size="14" />
      Aktiv
    </span>
  }
</div>

<div class="pointer-events-none flex min-w-0 flex-1 flex-col gap-2">
  <div class="min-w-0">
    <div class="flex items-center gap-1">
      <p class="min-w-0 flex-1 truncate text-sm font-medium">{{ design().name }}</p>
      @if (!isActive()) {
        <app-design-issues
          class="pointer-events-auto relative"
          [issues]="design().activation_issues" />
      }
    </div>
    <p hlmMuted class="text-xs">{{ sizeLabel }}</p>
  </div>

  <div class="pointer-events-auto relative mt-auto flex flex-col gap-2">
    <div class="flex items-center gap-2">
      <a
        hlmBtn
        variant="outline"
        size="sm"
        class="flex-1"
        [routerLink]="['/voucher-designs', design().public_id]"
        [attr.aria-label]="'Design ' + design().name + ' bearbeiten'">
        <ng-icon name="lucidePencil" />
        Bearbeiten
      </a>

      <!-- Dropdown on desktop, bottom sheet on phones. -->
      <button
        hlmBtn
        variant="outline"
        size="icon-sm"
        class="hidden sm:inline-flex"
        [disabled]="busy()"
        [hlmDropdownMenuTrigger]="actionsMenu"
        align="end"
        [attr.aria-label]="'Weitere Aktionen für ' + design().name">
        @if (previewing()) {
          <hlm-spinner />
        } @else {
          <ng-icon name="lucideEllipsis" />
        }
      </button>

      <hlm-sheet
        side="bottom"
        class="sm:hidden"
        [state]="sheetOpen() ? 'open' : 'closed'"
        (stateChanged)="sheetOpen.set($event === 'open')"
        (closed)="runPendingAction()">
        <button
          hlmSheetTrigger
          hlmBtn
          variant="outline"
          size="icon-sm"
          [disabled]="busy()"
          [attr.aria-label]="'Weitere Aktionen für ' + design().name">
          @if (previewing()) {
            <hlm-spinner />
          } @else {
            <ng-icon name="lucideEllipsis" />
          }
        </button>
        <hlm-sheet-content
          *hlmSheetPortal
          class="max-h-[85vh] gap-2 overflow-y-auto rounded-t-xl px-2 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div hlmSheetHeader class="px-3 pe-12">
            <h2 hlmSheetTitle class="truncate">{{ design().name }}</h2>
            <p hlmSheetDescription>{{ typeLabel }}</p>
          </div>
          <nav class="flex flex-col">
            <button
              hlmBtn
              variant="ghost"
              class="h-12 justify-start gap-3 text-base"
              [disabled]="previewBusy()"
              (click)="chooseAction(onPreview)">
              <ng-icon name="lucideDownload" />
              Beispiel-PDF herunterladen
            </button>
            <div hlmSeparator class="my-1"></div>
            <button
              hlmBtn
              variant="ghost"
              class="h-12 justify-start gap-3 text-base"
              (click)="chooseAction(onCopySame)">
              <ng-icon name="lucideCopy" />
              Duplizieren
            </button>
            <button
              hlmBtn
              variant="ghost"
              class="h-12 justify-start gap-3 text-base"
              (click)="chooseAction(onCopyOther)">
              <ng-icon name="lucideCopyPlus" />
              {{ copyLabel }}
            </button>
            <div hlmSeparator class="my-1"></div>
            <button
              hlmBtn
              variant="ghost"
              class="text-destructive hover:text-destructive hover:bg-destructive/10 h-12 justify-start gap-3 text-base"
              [disabled]="isActive()"
              (click)="chooseAction(onDelete)">
              <ng-icon name="lucideTrash2" />
              Löschen
            </button>
          </nav>
        </hlm-sheet-content>
      </hlm-sheet>
    </div>
  </div>
</div>

<ng-template #actionsMenu>
  <hlm-dropdown-menu class="w-64">
    <hlm-dropdown-menu-group>
      <button hlmDropdownMenuItem [disabled]="previewBusy()" (click)="onPreview()">
        <ng-icon name="lucideDownload" />
        Beispiel-PDF herunterladen
      </button>
    </hlm-dropdown-menu-group>
    <div hlmDropdownMenuSeparator></div>
    <hlm-dropdown-menu-group>
      <button hlmDropdownMenuItem (click)="onCopySame()">
        <ng-icon name="lucideCopy" />
        Duplizieren
      </button>
      <button hlmDropdownMenuItem (click)="onCopyOther()">
        <ng-icon name="lucideCopyPlus" />
        {{ copyLabel }}
      </button>
    </hlm-dropdown-menu-group>
    <div hlmDropdownMenuSeparator></div>
    <button hlmDropdownMenuItem variant="destructive" [disabled]="isActive()" (click)="onDelete()">
      <ng-icon name="lucideTrash2" />
      Löschen
    </button>
  </hlm-dropdown-menu>
</ng-template>
